import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Send, Plus, Megaphone, Trash2, PhoneOff, Pencil, Mail, Smartphone,
  Inbox, ArrowLeft,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { dateTime, humanise, relative } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, Checkbox, ConfirmDialog, EmptyState, Field, Input,
  LoadingBlock, Modal, PageHeader, Select, Tabs, Textarea, useToast, statusTone, Avatar,
} from '@/components/ui';
import type {
  Message, MessageTemplate, Announcement, Property, Tenant, MessageThread,
} from '@shared/types';

const PLACEHOLDERS = [
  '{{tenant_name}}', '{{property}}', '{{room}}', '{{amount_due}}',
  '{{balance}}', '{{due_date}}', '{{org_name}}', '{{link}}',
];

export default function Messages() {
  const { can } = useAuth();
  /*
   * Conversations first. The question this screen is usually opened with is
   * "has anybody written to me?", not "what did I send last Tuesday" — and a
   * tenant's message going unanswered for a week is the failure that costs
   * most.
   */
  const [tab, setTab] = useState('conversations');

  const { data: config } = useApi<{ configured: boolean; from: string | null }>('/messages/config');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        subtitle="Conversations with tenants, templates and notices — all sent by hand, never on a schedule"
      />

      {config && !config.configured && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-warn/10 border border-warn/25">
          <PhoneOff className="h-5 w-5 text-warn shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-ink">Text messaging is not enabled</p>
            <p className="text-muted mt-1 leading-relaxed">
              Messages are recorded with the status &ldquo;skipped&rdquo; so you can still see
              exactly what would have gone out. Get in touch and we will switch sending on.
            </p>
          </div>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'conversations', label: 'Conversations' },
          { id: 'history', label: 'History' },
          { id: 'templates', label: 'Templates' },
          { id: 'announcements', label: 'Notices' },
        ]}
      />

      {tab === 'conversations' && <Conversations />}
      {tab === 'history' && <MessageHistory />}
      {tab === 'templates' && <Templates canEdit={can('manager')} />}
      {tab === 'announcements' && <Announcements canEdit={can('staff')} />}
    </div>
  );
}

/**
 * Every tenant conversation, with whoever is waiting on an answer at the top.
 *
 * Two panes on a wide screen, one at a time on a phone — a manager reading
 * this on a doorstep needs the thread, not the list.
 */
/**
 * What became of one message, in as few words as are true.
 *
 * Three different facts, and they are not interchangeable. A portal message
 * that has been read was watched being read. An email that was "opened" had a
 * tracking image fetched by something — which Apple Mail does on arrival,
 * before anybody has seen it, and which a client with images off never does at
 * all. A bounce is the only one of the three that is certain in both
 * directions.
 *
 * So the wording differs: read, opened, delivered. Flattening them into one
 * tick would make the reliable one worthless.
 */
function deliveryNote(message: Message): { text: string; tone: 'ok' | 'muted' | 'danger' } | null {
  if (message.direction === 'inbound') return null;

  if (message.status === 'failed') {
    return { text: message.error_message ? `Failed — ${message.error_message}` : 'Failed', tone: 'danger' };
  }
  if (message.status === 'skipped') return { text: 'Not sent', tone: 'danger' };

  if (message.channel === 'portal') {
    return message.read_at
      ? { text: `Read ${relative(message.read_at)}`, tone: 'ok' }
      : { text: 'Not read yet', tone: 'muted' };
  }

  if (message.channel === 'email') {
    if (message.opened_at) return { text: `Opened ${relative(message.opened_at)}`, tone: 'ok' };
    if (message.delivered_at) return { text: 'Delivered', tone: 'muted' };
    return null;
  }

  return message.delivered_at ? { text: 'Delivered', tone: 'muted' } : null;
}

/**
 * A tenant nobody has written to yet, in the shape the thread pane expects.
 *
 * The threads endpoint only knows about conversations that exist, which is
 * right — but it meant this screen could only ever continue a conversation,
 * never begin one. Starting one had to be done from the tenant's own record,
 * which is a strange place to look for a screen called Messages.
 */
function draftThreadFor(tenant: Tenant): MessageThread {
  return {
    tenant_id: tenant.id,
    tenant_name: tenant.full_name,
    phone: tenant.phone,
    email: tenant.email,
    contact_preference: tenant.contact_preference ?? 'both',
    portal_enabled: Boolean(tenant.portal_enabled),
    last_message_at: null,
    last_body: null,
    last_direction: null,
    last_channel: null,
    awaiting_reply: 0,
    total: 0,
  };
}

function Conversations() {
  const [openTenant, setOpenTenant] = useState<string | null>(null);
  const [draft, setDraft] = useState<MessageThread | null>(null);
  const [picking, setPicking] = useState(false);
  const { data, loading, reload } = useApi<{ threads: MessageThread[]; awaiting_reply: number }>(
    '/messages/threads',
  );

  const threads = data?.threads ?? [];
  const active = threads.find((t) => t.tenant_id === openTenant)
    ?? (draft && draft.tenant_id === openTenant ? draft : null);

  const startWith = (tenant: Tenant) => {
    setDraft(draftThreadFor(tenant));
    setOpenTenant(tenant.id);
    setPicking(false);
  };

  const picker = picking ? (
    <TenantPicker onClose={() => setPicking(false)} onPick={startWith} />
  ) : null;

  if (loading && !data) return <LoadingBlock rows={5} />;

  if (threads.length === 0 && !active) {
    return (
      <>
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No conversations yet"
          message="Write to a tenant, or wait for one to write to you from their account — either way the thread appears here."
          action={(
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setPicking(true)}>
              New message
            </Button>
          )}
        />
        {picker}
      </>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      {picker}
      <Card className={cnIf(Boolean(active), 'hidden lg:block', '')}>
        <CardHeader
          title="Tenants"
          subtitle={data && data.awaiting_reply > 0
            ? `${data.awaiting_reply} waiting on a reply`
            : 'Nobody is waiting on you'}
          action={(
            <Button size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setPicking(true)}>
              New
            </Button>
          )}
        />
        <ul className="divide-y divide-line max-h-[70vh] overflow-y-auto">
          {(draft && !threads.some((t) => t.tenant_id === draft.tenant_id)
            ? [draft].concat(threads) : threads).map((thread) => (
            <li key={thread.tenant_id}>
              <button
                onClick={() => setOpenTenant(thread.tenant_id)}
                className={`w-full text-left px-4 py-3 transition-colors hover:bg-raised
                  ${thread.tenant_id === openTenant ? 'bg-raised' : ''}`}
              >
                <div className="flex items-center gap-2.5">
                  <Avatar name={thread.tenant_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{thread.tenant_name}</p>
                    <p className="text-xs text-faint truncate">
                      {thread.last_body
                        ? `${thread.last_direction === 'inbound' ? '' : 'You: '}${thread.last_body}`
                        : 'Nothing sent yet'}
                    </p>
                  </div>
                  {thread.awaiting_reply > 0 && (
                    <Badge tone="warn">{thread.awaiting_reply}</Badge>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {active ? (
        <Thread thread={active} onBack={() => setOpenTenant(null)} onSent={reload} />
      ) : (
        <Card className="hidden lg:flex items-center justify-center min-h-[320px]">
          <p className="text-sm text-muted">Choose a tenant to read the conversation.</p>
        </Card>
      )}
    </div>
  );
}

/** One tenant's thread, with the reply box under it. */
function Thread({ thread, onBack, onSent }: {
  thread: MessageThread;
  onBack: () => void;
  onSent: () => void;
}) {
  const toast = useToast();
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  /*
   * How this reply travels.
   *
   * The tab could only write into the tenant's account, which is the right
   * default — it keeps the whole conversation in one place and the tenant gets
   * told it is waiting. But a manager chasing somebody who never signs in
   * wants the words themselves to arrive on the phone, and having to leave for
   * the reminder queue to do it is the kind of detour that ends in a personal
   * mobile being used instead.
   */
  const [channel, setChannel] = useState<'portal' | 'sms' | 'email'>('portal');
  const { data, loading, reload } = useApi<Message[]>('/messages', {
    tenant_id: thread.tenant_id, limit: 100,
  });

  const messages = (data ?? []).slice().reverse();
  const segments = Math.max(1, Math.ceil(body.trim().length / 160));

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      if (channel === 'portal') {
        const result = await api.post('/messages/portal', {
          tenant_id: thread.tenant_id, body: text,
        });
        setBody('');
        reload();
        onSent();
        if (result.notified) {
          toast.success('Sent. They have been told it is waiting.');
        } else {
          /*
           * Say so rather than implying delivery. The reply is saved either way
           * and the tenant will see it when they sign in — but a manager who
           * thinks a text went out will not follow it up.
           */
          toast.info(
            `Saved to their account, but we could not notify them: ${
              (result.notify_problems ?? []).join('; ') || 'no contact details'
            }`,
          );
        }
      } else {
        const result = await api.post('/messages/send', {
          tenant_ids: [thread.tenant_id], body: text, channel, purpose: 'manual',
        });
        setBody('');
        reload();
        onSent();
        const failure = (result.results ?? [])[0];
        if (result.sent > 0) {
          toast.success(channel === 'sms' ? 'Text sent' : 'Email sent');
        } else {
          toast.info(failure?.error || 'That could not be delivered');
        }
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'That could not be sent');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader
        title={thread.tenant_name}
        subtitle={[
          thread.phone,
          thread.email,
          thread.contact_preference === 'none'
            ? 'has asked not to be contacted'
            : thread.contact_preference === 'email' ? 'prefers email'
            : thread.contact_preference === 'sms' ? 'prefers texts' : null,
        ].filter(Boolean).join(' · ')}
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" className="lg:hidden" onClick={onBack}
              icon={<ArrowLeft className="h-3.5 w-3.5" />}>
              Back
            </Button>
            <Link to={`/app/tenants/${thread.tenant_id}`}>
              <Button size="sm">Open tenant</Button>
            </Link>
          </div>
        }
      />

      <div className="flex-1 p-4 sm:p-5 space-y-3 max-h-[52vh] overflow-y-auto">
        {loading && !data ? <LoadingBlock rows={3} /> : messages.length === 0 ? (
          <p className="text-sm text-muted">Nothing has been sent yet.</p>
        ) : messages.map((message) => {
          const fromTenant = message.direction === 'inbound';
          return (
            <div key={message.id} className={`flex ${fromTenant ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 space-y-1 border ${
                  fromTenant ? 'bg-surface border-line' : 'bg-brand/10 border-brand/25'
                }`}
              >
                {message.subject && <p className="text-sm font-semibold">{message.subject}</p>}
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.body}</p>
                <p className="text-[11px] text-faint flex items-center gap-1.5 pt-0.5">
                  {message.channel === 'email' ? <Mail className="h-3 w-3" />
                    : message.channel === 'portal' ? <MessageSquare className="h-3 w-3" />
                    : <Smartphone className="h-3 w-3" />}
                  {fromTenant ? thread.tenant_name : (message.sent_by_name ?? 'You')}
                  {' · '}
                  {relative(message.created_at)}
                </p>
                {(() => {
                  const note = deliveryNote(message);
                  if (!note) return null;
                  return (
                    <p className={`text-[11px] ${
                      note.tone === 'danger' ? 'text-danger'
                        : note.tone === 'ok' ? 'text-ok' : 'text-faint'
                    }`}>
                      {note.text}
                    </p>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-4 sm:p-5 border-t border-line space-y-3">
        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Reply to ${thread.tenant_name}…`}
          maxLength={4000}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Select
            className="w-auto"
            value={channel}
            onChange={(e) => setChannel(e.target.value as 'portal' | 'sms' | 'email')}
          >
            <option value="portal">Send to their account</option>
            <option value="sms" disabled={!thread.phone}>Send as a text message</option>
            <option value="email" disabled={!thread.email}>Send as an email</option>
          </Select>
          <p className="text-xs text-faint flex-1 min-w-[12rem]">
            {channel === 'sms'
              ? `Goes straight to ${thread.phone} — ${segments} text${segments === 1 ? '' : 's'}. It is saved here too.`
              : channel === 'email'
              ? `Goes straight to ${thread.email}. Replies come back to your contact address.`
              : thread.portal_enabled
              ? 'Your reply goes to their account. They are told it is waiting — in full by email, or a short note by text.'
              : 'This tenant has no portal access, so they will not be able to read a reply. Switch it on from their record first.'}
          </p>
          <Button
            variant="primary"
            onClick={send}
            loading={sending}
            disabled={body.trim().length === 0}
            icon={<Send className="h-4 w-4" />}
          >
            {channel === 'portal' ? 'Reply' : 'Send'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Choosing who to write to.
 *
 * Shows everyone, including tenants who cannot be reached, and says which is
 * which rather than hiding them. Somebody looking for a name and not finding
 * it assumes the software is broken; somebody who sees "no phone or email on
 * file" knows exactly what to go and fix.
 */
function TenantPicker({ onClose, onPick }: {
  onClose: () => void;
  onPick: (tenant: Tenant) => void;
}) {
  const [query, setQuery] = useState('');
  const { data, loading } = useApi<Tenant[]>('/tenants');

  const term = query.trim().toLowerCase();
  const tenants = (data ?? [])
    // Past tenants stay reachable: a deposit return or a final bill is exactly
    // the conversation somebody needs to start after they have moved out.
    .filter((t) => t.status !== 'blacklisted')
    .filter((t) => !term
      || t.full_name.toLowerCase().includes(term)
      || String(t.phone ?? '').includes(term)
      || String(t.email ?? '').toLowerCase().includes(term))
    .sort((a, b) => a.full_name.localeCompare(b.full_name));

  return (
    <Modal open onClose={onClose} title="Who are you writing to?">
      <div className="space-y-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, phone or email"
          autoFocus
        />

        {loading && !data ? <LoadingBlock rows={3} /> : (
          <ul className="divide-y divide-line max-h-[50vh] overflow-y-auto -mx-1">
            {tenants.map((tenant) => {
              const reachable = Boolean(tenant.phone || tenant.email);
              return (
                <li key={tenant.id}>
                  <button
                    onClick={() => onPick(tenant)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-raised
                               transition-colors flex items-center gap-2.5"
                  >
                    <Avatar name={tenant.full_name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{tenant.full_name}</p>
                      <p className="text-xs text-faint truncate">
                        {[tenant.phone, tenant.email].filter(Boolean).join(' · ')
                          || 'No phone or email on file'}
                      </p>
                    </div>
                    {tenant.contact_preference === 'none' && (
                      <Badge tone="warn">Asked not to be contacted</Badge>
                    )}
                    {!reachable && tenant.contact_preference !== 'none' && (
                      <Badge tone="warn">Portal only</Badge>
                    )}
                  </button>
                </li>
              );
            })}
            {tenants.length === 0 && (
              <li className="px-3 py-6 text-sm text-muted text-center">
                {term ? 'Nobody by that name.' : 'No tenants yet.'}
              </li>
            )}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/** Tiny helper so the list can hide itself on a phone once a thread is open. */
function cnIf(condition: boolean, whenTrue: string, whenFalse: string) {
  return condition ? whenTrue : whenFalse;
}

function MessageHistory() {
  const [status, setStatus] = useState('');
  const { data, loading } = useApi<Message[]>('/messages', { status: status || undefined, limit: 200 });
  const messages = data ?? [];

  return (
    <Card>
      <CardHeader
        title="Sent messages"
        action={
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[140px]">
            <option value="">Any status</option>
            {['sent', 'delivered', 'failed', 'undelivered', 'skipped'].map((s) => (
              <option key={s} value={s}>{humanise(s)}</option>
            ))}
          </Select>
        }
      />
      <div className="p-5 sm:p-6 pt-4">
        {loading ? (
          <LoadingBlock rows={5} />
        ) : messages.length === 0 ? (
          <EmptyState
            icon={<MessageSquare className="h-6 w-6" />}
            title="No messages sent yet"
            message="Send reminders from the reminder queue, or message a tenant from their page."
            action={<Link to="/app/reminders"><Button variant="primary" size="sm">Open reminder queue</Button></Link>}
          />
        ) : (
          <div className="space-y-3">
            {messages.map((m) => (
              <div key={m.id} className="p-4 rounded-xl border border-line bg-raised/40">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {m.tenant_name && <Avatar name={m.tenant_name} size="sm" />}
                    <div className="min-w-0">
                      {m.tenant_id ? (
                        <Link to={`/app/tenants/${m.tenant_id}`} className="font-medium hover:text-brand truncate block">
                          {m.tenant_name}
                        </Link>
                      ) : (
                        <span className="font-medium">{m.to_phone}</span>
                      )}
                      <p className="text-xs text-faint">{m.to_phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge tone="neutral">{humanise(m.purpose)}</Badge>
                    <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                  </div>
                </div>

                <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">{m.body}</p>

                <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-line/60">
                  <span className="text-xs text-faint">
                    {m.sent_by_name ? `Sent by ${m.sent_by_name} · ` : ''}
                    {dateTime(m.created_at)}
                  </span>
                  {m.segments && (
                    <span className="text-xs text-faint">{m.segments} segment{m.segments === 1 ? '' : 's'}</span>
                  )}
                </div>

                {m.error_message && (
                  <p className="text-xs text-danger mt-2">{m.error_message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function Templates({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MessageTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<MessageTemplate[]>('/message-templates');
  const templates = data ?? [];

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/message-templates/${deleting.id}`);
      toast.success('Template deleted');
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Message templates"
          subtitle="Placeholders are filled in per tenant when you send"
          action={canEdit && (
            <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
              New template
            </Button>
          )}
        />
        <div className="p-5 sm:p-6 pt-4">
          {loading ? (
            <LoadingBlock rows={4} />
          ) : templates.length === 0 ? (
            <EmptyState icon={<MessageSquare className="h-6 w-6" />} title="No templates yet" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {templates.map((t) => (
                <div key={t.id} className="p-4 rounded-xl border border-line bg-raised/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{t.name}</p>
                      <Badge tone="neutral" className="mt-1">{t.category}</Badge>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(t)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleting(t)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted mt-3 leading-relaxed whitespace-pre-wrap">{t.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {editing && (
        <TemplateModal
          template={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
        loading={busy}
        title="Delete this template?"
        confirmLabel="Delete"
        message="Messages already sent with it are unaffected."
      />
    </>
  );
}

function TemplateModal({ template, onClose, onDone }: {
  template?: MessageTemplate; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(template?.name ?? '');
  const [category, setCategory] = useState(template?.category ?? 'general');
  const [body, setBody] = useState(template?.body ?? '');

  function insert(placeholder: string) {
    setBody((b) => `${b}${placeholder}`);
  }

  async function submit() {
    if (!name.trim() || !body.trim()) { toast.error('Give it a name and a message'); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), category, body: body.trim() };
      if (template) await api.patch(`/message-templates/${template.id}`, payload);
      else await api.post('/message-templates', payload);
      toast.success('Template saved');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={template ? 'Edit template' : 'New template'}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rent due soon" autoFocus />
          </Field>
          <Field label="Category" hint="Used to suggest a template in the reminder queue">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {['rent', 'receipt', 'lease', 'document', 'notice', 'general'].map((c) => (
                <option key={c} value={c}>{humanise(c)}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Message" required>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Hi {{tenant_name}}, a reminder that {{amount_due}} is due on {{due_date}}."
          />
        </Field>

        <div>
          <p className="label mb-2">Insert a placeholder</p>
          <div className="flex flex-wrap gap-1.5">
            {PLACEHOLDERS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => insert(p)}
                className="px-2 py-1 rounded-lg text-xs font-mono bg-raised border border-line
                           text-muted hover:text-brand hover:border-brand/40 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="p-3 rounded-xl bg-raised border border-line">
          <p className="text-xs text-faint">
            {body.length} characters · roughly {body.length <= 160 ? 1 : Math.ceil(body.length / 153)} SMS
            segment{body.length <= 160 ? '' : 's'} before placeholders are filled in
          </p>
        </div>
      </div>
    </Modal>
  );
}

function Announcements({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Announcement | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<Announcement[]>('/announcements');
  const announcements = data ?? [];

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/announcements/${deleting.id}`);
      toast.success('Notice removed');
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Notices"
          subtitle="Posted to the tenant portal, and optionally texted out"
          action={canEdit && (
            <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              Post a notice
            </Button>
          )}
        />
        <div className="p-5 sm:p-6 pt-4">
          {loading ? (
            <LoadingBlock rows={3} />
          ) : announcements.length === 0 ? (
            <EmptyState
              icon={<Megaphone className="h-6 w-6" />}
              title="No notices posted"
              message="Post water shut-offs, payment instructions or anything else tenants should see."
              action={canEdit && <Button variant="primary" size="sm" onClick={() => setCreating(true)}>Post a notice</Button>}
            />
          ) : (
            <div className="space-y-3">
              {announcements.map((a) => (
                <div key={a.id} className="p-4 rounded-xl border border-line bg-raised/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{a.title}</p>
                        {a.is_pinned && <Badge tone="brand">Pinned</Badge>}
                        <Badge tone="neutral">{a.property_name ?? 'All properties'}</Badge>
                      </div>
                      <p className="text-sm text-muted mt-2 whitespace-pre-wrap leading-relaxed">{a.body}</p>
                      <p className="text-xs text-faint mt-2">{relative(a.published_at)}</p>
                    </div>
                    {canEdit && (
                      <Button size="icon" variant="ghost" onClick={() => setDeleting(a)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {creating && (
        <AnnouncementModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); reload(); }} />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
        loading={busy}
        title="Remove this notice?"
        confirmLabel="Remove"
        message="It will disappear from the tenant portal. Any SMS already sent is unaffected."
      />
    </>
  );
}

function AnnouncementModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [alsoSms, setAlsoSms] = useState(false);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data: tenants } = useApi<Tenant[]>('/tenants', { status: 'active' });

  const recipientCount = propertyId
    ? (tenants ?? []).filter((t) => t.property_id === propertyId).length
    : (tenants ?? []).length;

  async function submit() {
    if (!title.trim() || !body.trim()) { toast.error('Add a title and a message'); return; }
    setBusy(true);
    try {
      const res = await api.post<{ sms: { sent: number } | null }>('/announcements', {
        title: title.trim(),
        body: body.trim(),
        property_id: propertyId || null,
        is_pinned: isPinned,
        also_sms: alsoSms,
      });
      toast.success(res.sms ? `Notice posted and texted to ${res.sms.sent} tenants` : 'Notice posted');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not post the notice');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Post a notice"
      subtitle="Appears in the tenant portal immediately"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}
            icon={alsoSms ? <Send className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}>
            {alsoSms ? `Post and text ${recipientCount}` : 'Post notice'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Water tank cleaning — Saturday"
            autoFocus
          />
        </Field>

        <Field label="Message" required>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="The main water tank will be cleaned this Saturday between 9am and 1pm…"
          />
        </Field>

        <Field label="Who sees it">
          <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
            <option value="">Every tenant</option>
            {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name} only</option>)}
          </Select>
        </Field>

        <div className="space-y-3">
          <Checkbox
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            label="Pin to the top"
          />
          <Checkbox
            checked={alsoSms}
            onChange={(e) => setAlsoSms(e.target.checked)}
            label="Also send as an SMS"
            description={`Texts ${recipientCount} tenant${recipientCount === 1 ? '' : 's'} right now — this costs money and cannot be undone`}
          />
        </div>
      </div>
    </Modal>
  );
}
